import { ElementType } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink as RouterNavLink, useLocation } from 'react-router';

import { MAX_NAV_COUNTER } from '../../constants/sidebar';
import type { SidebarTooltipWrapper } from '../../types/sidebarTooltipWrapper';

const stylesheet = {
  link: 'relative flex items-center text-semantic-text-primary rounded-semantic',
  iconSlot: 'flex-shrink-0',
  label: 'ml-3 text-left text-sm whitespace-nowrap overflow-hidden',
  badge:
    'ml-auto inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold bg-semantic-bg-elevated text-semantic-text-primary border border-semantic-border',
  badgeCollapsed:
    'absolute -top-1 -right-1 min-w-[21px] h-[21px] flex items-center justify-center rounded-full text-[10px] font-bold bg-semantic-bg-elevated text-semantic-text-primary border-2 border-semantic-bg-surface',
  linkActive: 'bg-semantic-bg-active text-semantic-text-accent font-semibold',
  linkInactive: 'hover:bg-semantic-bg-hover',
  linkExpanded: 'pl-3 pr-2 py-2',
  linkCollapsed: 'justify-center p-2',
};

export interface GenericNavLinkProps {
  icon: ElementType;
  text: string;
  href: string;
  isOpen: boolean;
  count?: number;
  isPartial?: boolean;
  showCounter?: boolean;
  onNavigate?: () => void;
  matchNestedRoutes?: boolean;
  tooltipWrapper?: SidebarTooltipWrapper;
  dataTestId?: string;
}

function isNavLinkActive(
  pathname: string,
  search: string,
  url: URL,
  matchNestedRoutes: boolean,
): boolean {
  if (url.search) {
    if (pathname !== url.pathname) {
      return false;
    }

    const currentParams = new URLSearchParams(search);
    const hrefParams = new URLSearchParams(url.search);

    return currentParams.toString() === hrefParams.toString();
  }

  if (matchNestedRoutes) {
    return pathname === url.pathname || pathname.startsWith(`${url.pathname}/`);
  }

  return pathname === url.pathname;
}

function shouldShowCounter(count?: number, showCounter?: boolean): boolean {
  if (showCounter === false) {
    return false;
  }

  return count !== undefined && count !== 0;
}

function getRenderedCount(
  t: ReturnType<typeof useTranslation>['t'],
  count?: number,
  isPartial?: boolean,
) {
  if ((count ?? 0) > MAX_NAV_COUNTER) {
    return t('common.maxEmailCounter');
  }
  if (isPartial) {
    return t('common.partialCounter', { count });
  }
  return count;
}

export function GenericNavLink({
  icon: Icon,
  text,
  href,
  isOpen,
  count,
  isPartial,
  showCounter,
  onNavigate,
  matchNestedRoutes = false,
  tooltipWrapper: TooltipWrapper,
  dataTestId,
}: Readonly<GenericNavLinkProps>) {
  const { t } = useTranslation();
  const location = useLocation();

  const url = new URL(href, window.location.origin);
  const isActive = isNavLinkActive(
    location.pathname,
    location.search,
    url,
    matchNestedRoutes,
  );

  const renderedCount = getRenderedCount(t, count, isPartial);
  const collapsedLabel = isOpen ? undefined : t(text);
  const shouldWrap = Boolean(TooltipWrapper) && !isOpen;

  const link = (
    <RouterNavLink
      to={href}
      data-testid={dataTestId}
      onClick={onNavigate}
      title={shouldWrap ? undefined : collapsedLabel}
      aria-label={collapsedLabel}
      className={`${stylesheet.link} ${isActive ? stylesheet.linkActive : stylesheet.linkInactive} ${isOpen ? stylesheet.linkExpanded : stylesheet.linkCollapsed}`}
    >
      <Icon className={stylesheet.iconSlot} />
      {isOpen && <span className={stylesheet.label}>{t(text)}</span>}
      {shouldShowCounter(count, showCounter) && (
        <span className={isOpen ? stylesheet.badge : stylesheet.badgeCollapsed}>
          {renderedCount}
        </span>
      )}
    </RouterNavLink>
  );

  if (TooltipWrapper && shouldWrap) {
    return <TooltipWrapper label={t(text)}>{link}</TooltipWrapper>;
  }

  return link;
}
